<script setup lang="ts">
import { AboutPageHeader } from '~/components/ui/about-page-header'
import type { TAboutProductHeader } from '~/components/widget/about-page'
import { useDateTimeFormat } from '~/composables/useDateTimeFormat'
import { microcreditsTypes } from '~/data/pages/microcredit/mocks'
import { useRating } from '~/composables/useRating'

const { t } = useI18n()

const props = defineProps<{
  product: TAboutProductHeader
  type:
    | 'credit'
    | 'deposit'
    | 'vacancy'
    | 'money-transfer'
    | 'insurance'
    | 'microcredit'
    | 'card'
    | 'crypto'
}>()

const route = useRoute()
const config = useRuntimeConfig()
const pageUrl = `${config.public.assetUrl}${route.path}`

// Fetch rating stats for money-transfer and insurance types
const { getRatingStats } = useRating()
const shouldFetchRatings = ['money-transfer', 'insurance'].includes(props.type)
const rateableType = props.type === 'insurance' ? 'insurance_company' : 'money_transfer'
const { data: ratingStatsData } = shouldFetchRatings
  ? getRatingStats(rateableType, props.product.id)
  : { data: ref(null) }
const ratingStats = computed(() => ratingStatsData.value?.data || null)

const getMicrocreditTypeTitleByType = (type: string) => {
  const item = microcreditsTypes.find((microcredit) => microcredit.type === type)

  return item ? t(item.singularTitle) : t('common.mfc')
}

if (props.type === 'microcredit') {
  useSchemaOrg([
    defineOrganization({
      name: props.product.title,
      alternateName: props.product.organization.official_title || undefined,
      url: pageUrl,
      description: useStripTags(props.product.description),
      logo: props.product.logo
        ? {
            '@type': 'ImageObject',
            url: props.product.logo,
            caption: props.product.title
          }
        : undefined,
      image: props.product.logo ? props.product.logo : undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: props.product.head_branch_location || '',
        addressLocality: props.product.head_branch_location || '',
        addressCountry: 'KG'
      },
      telephone: props.product.call_center_phone || undefined,
      '@type': ['Organization', 'FinancialService'],

      knowsAbout: t('schema.about_page_header.microcredit_knows_about'),
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: t('schema.about_page_header.microcredit_services'),
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'LoanOrCredit',
              name: t('common.microcredits')
            }
          }
        ]
      }
    })
  ])
} else if (props.type === 'crypto') {
  useSchemaOrg([
    defineOrganization({
      name: props.product.title,
      alternateName: props.product.organization.official_title || undefined,
      url: pageUrl,
      description: useStripTags(props.product.description),
      logo: props.product.logo
        ? {
            '@type': 'ImageObject',
            url: props.product.logo,
            caption: props.product.title
          }
        : undefined,
      image: props.product.logo ? props.product.logo : undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: props.product.head_branch_location || '',
        addressLocality: props.product.head_branch_location || '',
        addressCountry: 'KG'
      },
      telephone: props.product.call_center_phone || undefined,
      '@type': ['Organization', 'FinancialService'],

      knowsAbout: t('common.crypto_exchange'),
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: t('schema.about_page_header.crypto_services'),
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: t('common.crypto_exchange') }
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: t('schema.about_page_header.crypto_buy') }
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: t('schema.about_page_header.crypto_sell') }
          }
        ]
      }
    })
  ])
} else if (['credit', 'deposit', 'card'].includes(props.type)) {
  let schemaType: string

  if (props.type === 'credit') schemaType = 'LoanOrCredit'
  else if (props.type === 'deposit') schemaType = 'DepositAccount'
  else schemaType = 'PaymentCard'

  useSchemaOrg([
    defineProduct({
      '@type': 'Product',
      name: props.product.title,
      description: useStripTags(props.product.description),
      url: pageUrl,
      brand: {
        '@type': 'Brand',
        name: props.product.organization.official_title,
        url: props.product.organization.url || undefined
      },
      image: props.product.logo ? props.product.logo : undefined,
      provider: {
        '@type': 'FinancialService',
        name: props.product.organization.official_title,
        telephone: props.product.call_center_phone || undefined,
        address: {
          '@type': 'PostalAddress',
          streetAddress: props.product.head_branch_location || '',
          addressLocality: props.product.head_branch_location || '',
          addressCountry: 'KG'
        }
      },
      offers: {
        '@type': 'Offer',
        url: pageUrl,
        priceCurrency: 'KGS',
        price: props.product.amount_from || '500',
        description:
          props.type === 'credit'
            ? props.product?.is_islamic
              ? t('schema.about_page_header.credit_conditions_islamic')
              : t('schema.about_page_header.credit_conditions')
            : t('schema.about_page_header.deposit_conditions'),
        availability: 'https://schema.org/InStock',
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'KG',
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
        },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0',
            currency: 'KGS'
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'KG'
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 0,
              unitCode: 'DAY'
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 0,
              unitCode: 'DAY'
            }
          }
        },
        eligibleRegion: {
          '@type': 'Country',
          name: 'KG'
        },
        validFrom: props.product.updated_at || new Date().toISOString()
      },
      // keep original LoanOrCredit / DepositAccount for semantic meaning
      isAccessoryOrSparePartFor: {
        '@type': schemaType,
        name: props.product.title,
        description: useStripTags(props.product.description),
        url: pageUrl
      }
    })
  ])
} else if (['money-transfer', 'insurance'].includes(props.type)) {
  useSchemaOrg([
    defineProduct(
      computed(() => {
        const schemaData: Record<string, unknown> = {
          '@type': 'Product',
          name: props.product.title,
          description: useStripTags(props.product.description) || props.product.title,
          url: pageUrl,
          brand: {
            '@type': 'Brand',
            name: props.product.organization.official_title,
            url: props.product.organization.url || undefined
          },
          image: props.product.logo ? props.product.logo : undefined,
          provider: {
            '@type': 'FinancialService',
            name: props.product.organization.official_title,
            telephone: props.product.call_center_phone || undefined,
            address: {
              '@type': 'PostalAddress',
              streetAddress: props.product.head_branch_location || '',
              addressLocality: props.product.head_branch_location || '',
              addressCountry: 'KG'
            }
          }
        }

        // Add offers for money-transfer (with commission data) or insurance (generic service)
        if (props.type === 'money-transfer') {
          let price = '0'
          if (props.product.commissions?.length) {
            const fixedCommissions = props.product.commissions
              .filter((c) => c.type === 'fix' && c.value > 0)
              .map((c) => c.value)
            if (fixedCommissions.length > 0) {
              price = Math.min(...fixedCommissions).toString()
            }
          }
          schemaData.offers = {
            '@type': 'Offer',
            url: pageUrl,
            priceCurrency: 'KGS',
            price,
            description: t('schema.about_page_header.money_transfer_service', {
              title: props.product.organization.official_title
            }),
            availability: 'https://schema.org/InStock',
            hasMerchantReturnPolicy: {
              '@type': 'MerchantReturnPolicy',
              applicableCountry: 'KG',
              returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                value: '0',
                currency: 'KGS'
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'KG'
              },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 0,
                  unitCode: 'DAY'
                },
                transitTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 0,
                  unitCode: 'DAY'
                }
              }
            },
            eligibleRegion: {
              '@type': 'Country',
              name: 'KG'
            }
          }
        } else if (props.type === 'insurance') {
          schemaData.offers = {
            '@type': 'Offer',
            url: pageUrl,
            priceCurrency: 'KGS',
            price: '0',
            description: t('schema.about_page_header.insurance_service', {
              title: props.product.organization.official_title
            }),
            availability: 'https://schema.org/InStock',
            hasMerchantReturnPolicy: {
              '@type': 'MerchantReturnPolicy',
              applicableCountry: 'KG',
              returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                value: '0',
                currency: 'KGS'
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'KG'
              },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 0,
                  unitCode: 'DAY'
                },
                transitTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 0,
                  unitCode: 'DAY'
                }
              }
            },
            eligibleRegion: {
              '@type': 'Country',
              name: 'KG'
            }
          }
        }

        // Add aggregateRating if rating stats exist
        if (ratingStats.value && ratingStats.value.total_ratings > 0) {
          schemaData.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: ratingStats.value.overall_average.toFixed(1),
            reviewCount: ratingStats.value.total_ratings,
            bestRating: 5,
            worstRating: 1
          }
        }

        return schemaData
      })
    )
  ])
} else if (props.type === 'vacancy') {
  const extractEmploymentType = (product: TAboutProductHeader) => {
    const employmentTypes = product.employment_type || {}
    const types: string[] = []

    // Collect all employment types that are true
    if (employmentTypes.full_time) types.push('FULL_TIME')
    if (employmentTypes.part_time) types.push('PART_TIME')
    if (employmentTypes.contractor) types.push('CONTRACTOR')
    if (employmentTypes.temporary) types.push('TEMPORARY')
    if (employmentTypes.intern) types.push('INTERN')
    if (employmentTypes.volunteer) types.push('VOLUNTEER')
    if (employmentTypes.per_diem) types.push('PER_DIEM')
    if (employmentTypes.other) types.push('OTHER')

    return types.length > 0 ? types : undefined
  }

  const extractSalary = (product: TAboutProductHeader) => {
    if (product.base_salary) {
      return {
        '@type': 'MonetaryAmount',
        currency: product.salary_currency || 'KGS',
        value: {
          '@type': 'QuantitativeValue',
          value: product.base_salary,
          unitText: 'MONTH'
        }
      }
    }

    return undefined
  }

  const getValidThrough = (datePosted: string) => {
    const posted = new Date(datePosted)
    const validThrough = new Date(posted)
    validThrough.setMonth(posted.getMonth() + 2) // Valid for 2 months
    return validThrough.toISOString()
  }

  const descriptionText = props.product.description || ''
  const postedDate = props.product.updated_at || new Date().toISOString()

  const employmentType = extractEmploymentType(props.product)
  const baseSalary = extractSalary(props.product)

  const jobPostingSchema: Record<string, unknown> = {
    identifier: {
      '@type': 'PropertyValue',
      name: props.product.organization.official_title || props.product.organization.title,
      value: props.product.id?.toString() || '1'
    },
    title: props.product.title,
    description: useStripTags(descriptionText),
    url: pageUrl,
    datePosted: postedDate,
    validThrough: getValidThrough(postedDate),
    hiringOrganization: {
      '@type': 'Organization',
      name: props.product.organization.official_title || props.product.organization.title,
      sameAs: props.product.organization.website_url || undefined,
      logo: props.product.organization.logo ? props.product.organization.logo : undefined,
      email: props.product.organization.email || undefined,
      telephone: props.product.organization.call_center_phone || undefined
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: props.product.location?.title,
        addressCountry: 'KG'
      }
    }
  }

  // Only add optional fields if they exist
  if (employmentType) {
    jobPostingSchema.employmentType = employmentType
  }
  if (baseSalary) {
    jobPostingSchema.baseSalary = baseSalary
  }

  useSchemaOrg([defineJobPosting(jobPostingSchema)])
}

const statusComments = computed(() => {
  const comments: string[] = []

  const org = props.product?.organization
  const product = props.product

  if (org && !org.status) {
    if (product?.status !== 'active' && product?.status_comments) {
      comments.push(product.status_comments)
    }
  } else if (org.status !== 'active') {
    if (org.status_comments) {
      comments.push(org.status_comments)
    }
    if (product?.status_comments) {
      comments.push(product.status_comments)
    }
  } else if (product?.status !== 'active' && product?.status_comments) {
    comments.push(product.status_comments)
  }

  return comments
})
</script>

<template>
  <AboutPageHeader
    :title="product?.title || ''"
    :organization-title="product?.organization.official_title || ''"
    :type="type"
    :logo="product?.logo"
    :logo-rectangle="product?.logo_rectangle"
    :status-comments="statusComments"
    :updated-at="product?.updated_at ? useDateTimeFormat(product.updated_at) : undefined"
    :views-count="product?.views_count"
    :is-islamic="product?.is_islamic"
    :microcredit-type-title="getMicrocreditTypeTitleByType(product?.type)"
    :crypto-type-title="product?.main_type_title"
  >
    <slot />
  </AboutPageHeader>
</template>
